import * as React from 'react'
import * as PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import media from '../../../services/generateMediaQueries'

import Icon from '../Icon/Icon'

let INPUT_VARIANT = ''

const Control = styled.input`
  position: relative;
  width: 100%;
  height: 100%;
  padding: 0 1rem;
  font-weight: var(--font-weight);
  text-align: ${props => (props.centered ? 'center' : 'left')};

  outline: 0;
  background-color: transparent;

  border-radius: var(--border-radius-form);
  border: var(--border-form);
  transition-property: border-color, opacity;
  transition-duration: var(--animation-duration);
  transition-timing-function: var(--animation-easing);

  ${props =>
    props.hasError &&
    css`
      border-color: var(--color-error);
    `}

  &:disabled {
    opacity: 0.5;
  }

  &:focus {
    border-color: var(--color-primary);
  }

  &::placeholder {
    opacity: 0;
  }

  &:required {
    box-shadow: none !important;
  }

  ${() =>
    INPUT_VARIANT === 'centered' &&
    css`
      text-align: center;

      &::placeholder {
        opacity: 1;
      }
    `}

  ${() =>
    ['withIcon', 'search'].indexOf(INPUT_VARIANT) >= 0 &&
    css`
      padding-right: 3rem;
    `}
`

const Placeholder = styled.span`
  display: flex;
  align-items: center;
  user-select: none;
  border-radius: var(--border-radius-form);

  color: var(--color-fg-light);
  position: absolute;

  transition-property: font-size, height, background-color, padding, top, color;
  transition-duration: var(--animation-duration);
  transition-timing-function: var(--animation-easing);

  background-color: var(--color-bg-form);
  height: 1rem;
  padding: 0 0.25rem;
  font-size: 0.75em;
  top: 0;
  left: 0.75rem;
  pointer-events: none;
  overflow-x: visible;
  white-space: nowrap;

  ${props =>
    props.centered &&
    css`
      width: 100%;
      display: flex;
      justify-content: center;
      left: 0;
    `};

  &:empty {
    display: none;
  }

  ${Control}:placeholder-shown ~ & {
    background-color: transparent;
    top: 0.5rem;
    height: 3rem;
    padding: 0 0.25rem;
    font-size: 1rem;
  }

  ${props =>
    props.hasError &&
    css`
      color: var(--color-error);
      ${Control}:placeholder-shown ~ & {
        color: var(--color-fg-light);
      }
    `}

  ${Control}:focus ~ & {
    background-color: var(--color-bg-form);
    color: var(--color-primary);
    height: 1rem;
    padding: 0 0.25rem;
    font-size: 0.75em;
    top: 0;
    left: 0.75rem;

    ${props => {
      if (!props.floating) {
        return css`
          display: none;
        `
      }

      return null
    }}
  }

  ${Control}:not(:placeholder-shown) ~ & {
    ${props => {
      if (!props.floating) {
        return css`
          display: none;
        `
      }

      return null
    }}
  }

  ${() =>
    ['search', 'centered'].includes(INPUT_VARIANT) &&
    css`
      display: none;
    `}
`

const ControlBase = styled.label`
  position: relative;
  display: block;
  width: 100%;
  height: 3.5rem;
  padding-top: 0.5rem;
  font-size: 1rem;

  ${props =>
    props.isEconcierge &&
    css`
      padding-top: 0rem;
      height: 3rem;
    `}
`

const ComponentBase = styled.div`
  ${media.greaterThan(`small`)`
  margin-bottom: 0.625rem;
  `}
  ${props =>
    props.isEconcierge &&
    css`
      width: 100%;
    `}
`

const ErrorMessage = styled.output`
  display: block;
  color: var(--color-error);
  padding: 0 1rem;
  font-size: 0.75em;
  margin-top: 0.25rem;
  overflow: hidden;
  min-height: 1.125rem;
  transition-property: height, opacity;
  transition-duration: var(--animation-duration);
  transition-timing-function: var(--animation-easing);

  :empty {
    height: 0;
    opacity: 0;
  }

  ${ControlBase}:focus-within ~ & {
    height: 0;
    opacity: 0;
  }
`

const Hint = styled.span`
  z-index: 2;
  position: absolute;
  width: 3rem;
  height: 3rem;
  bottom: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--color-fg-light);
`

const IconWrapper = styled(Hint)`
  color: ${props => props.iconColor || 'inherit'};
`

class TextInput extends React.Component {
  input = React.createRef()

  constructor(props) {
    super(props)

    this.state = {
      passwordMask: true,
    }
  }

  componentDidMount() {
    const { initialValue } = this.props

    if (this.input.current) {
      this.input.current.value = initialValue
    }
  }

  componentDidUpdate(prevProps) {
    const { initialValue } = this.props

    if (prevProps.initialValue !== initialValue) {
      this.input.current.value = initialValue
    }
  }

  toggleMasking = maskState => {
    this.setState({
      passwordMask: maskState,
    })
  }

  render() {
    const {
      placeholder,
      error,
      touched,
      variant,
      iconName,
      iconColor,
      floatingLabel,
      centered,
      whiteOut,
      onIconClick,
      type,
      isEconcierge,
      ...props
    } = this.props

    const { passwordMask } = this.state

    INPUT_VARIANT = variant

    const iconProps = {}

    if (onIconClick) {
      iconProps.onClick = onIconClick
    }

    return (
      <ComponentBase isEconcierge={isEconcierge}>
        <ControlBase isEconcierge={isEconcierge}>
          {variant === 'search' && (
            <IconWrapper {...iconProps} iconColor={iconColor}>
              <Icon name="search" />
            </IconWrapper>
          )}
          {variant === 'withIcon' && iconName && (
            <IconWrapper iconColor={iconColor} {...iconProps}>
              <Icon name={iconName} />
            </IconWrapper>
          )}
          {variant === 'password' && (
            <IconWrapper
              iconColor={iconColor}
              onClick={() => {
                this.toggleMasking(!passwordMask)
              }}
            >
              <Icon name={passwordMask ? 'password-unmask' : 'password-mask'} />
            </IconWrapper>
          )}
          <Control
            {...props}
            type={
              variant === 'password'
                ? passwordMask
                  ? 'password'
                  : 'text'
                : props.type
            }
            centered={centered}
            placeholder={placeholder}
            ref={this.input}
            hasError={touched && error}
            whiteOut={whiteOut}
          />
          <Placeholder
            centered={centered}
            floating={floatingLabel}
            hasError={touched && error}
          >
            {placeholder}
          </Placeholder>
        </ControlBase>
        <ErrorMessage>{touched && error}</ErrorMessage>
      </ComponentBase>
    )
  }
}

TextInput.defaultProps = {
  placeholder: '',
  initialValue: '',
  error: null,
  touched: false,
  floatingLabel: true,
  centered: false,
}

TextInput.propTypes = {
  placeholder: PropTypes.string,
  initialValue: PropTypes.string,
  error: PropTypes.string,
  touched: PropTypes.bool,
  floatingLabel: PropTypes.bool,
  centered: PropTypes.bool,
}

export default TextInput
